import {addDoc, collection, serverTimestamp} from "firebase/firestore";
import {firestore} from "../firebase/config";
import {ref, uploadBytes, getDownloadURL, deleteObject} from "firebase/storage";
import {storage} from "../firebase/config";

export interface FeedbackData {
  type: "general" | "anonymous" | "bug";
  name?: string;
  email?: string;
  subject: string;
  message: string;
  // Bug feedback specific fields
  siteLocation?: string;
  expectedBehaviour?: string;
  observedBehaviour?: string;
  screenshotUrl?: string;
  timestamp?: any;
  userAgent?: string;
  ipAddress?: string;
  read?: boolean;
  readAt?: any;
}

export const uploadScreenshot = async (file: File): Promise<string> => {
  try {
    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
    if (!allowedTypes.includes(file.type)) {
      throw new Error("Only image files (JPEG, PNG) are allowed");
    }

    // Validate file size (5MB limit for screenshots)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      throw new Error("Screenshot size must be less than 5MB");
    }

    // Create storage reference
    const fileName = `feedback-screenshots/${Date.now()}-${file.name}`;
    const storageRef = ref(storage, fileName);

    // Upload file
    const uploadResult = await uploadBytes(storageRef, file);

    // Get download URL
    const downloadURL = await getDownloadURL(uploadResult.ref);

    return downloadURL;
  } catch (error) {
    console.error("Error uploading screenshot:", error);
    throw new Error("Failed to upload screenshot. Please try again.");
  }
};

export const deleteScreenshot = async (
  screenshotUrl: string
): Promise<void> => {
  try {
    // Extract file path from URL
    const url = new URL(screenshotUrl);
    let filePath: string | null = null;

    // Handle emulator URLs (format: /v0/b/bucket/o/path?alt=media)
    const emulatorMatch = url.pathname.match(/\/o\/(.+?)(?:\?|$)/);
    if (emulatorMatch) {
      filePath = decodeURIComponent(emulatorMatch[1]);
    } else {
      // Handle production URLs (format: https://storage.googleapis.com/bucket/path)
      const productionMatch = url.pathname.match(/^\/([^/]+)\/(.+)$/);
      if (productionMatch) {
        filePath = productionMatch[2]; // Skip the bucket name, just get the path
      }
    }

    if (filePath) {
      const fileRef = ref(storage, filePath);
      await deleteObject(fileRef);
    }
  } catch (error) {
    throw new Error("Failed to delete screenshot. Please try again.");
  }
};

export const submitFeedback = async (
  feedbackData: FeedbackData
): Promise<string> => {
  try {
    const feedbackCollection = collection(firestore, "feedback");

    // Filter out undefined values to prevent Firebase validation errors
    const cleanFeedbackData = Object.fromEntries(
      Object.entries(feedbackData).filter(([, value]) => value !== undefined)
    );

    const feedbackToSubmit = {
      ...cleanFeedbackData,
      timestamp: serverTimestamp(),
      userAgent: navigator.userAgent,
      // Note: IP address would need to be captured server-side for security
    };

    const docRef = await addDoc(feedbackCollection, feedbackToSubmit);
    return docRef.id;
  } catch (error) {
    console.error("Error submitting feedback:", error);
    // Error submitting feedback - throw user-friendly error
    throw new Error("Failed to submit feedback. Please try again.");
  }
};

export const getFeedbackCollection = () => {
  return collection(firestore, "feedback");
};
